/** @fileoverview Shared frame of the checklist and timeline blocks: title, rows, and one message line. */

import type { ReactNode } from 'react';

import { Skeleton } from '../ui/skeleton';

import { SAVE_APP_NOTE } from './helpers';
import InlineAlert from './InlineAlert';

type StateBlockProps = {
  title?: string;
  selectionPath?: string;
  /** False in the builder before the app is saved; the block then shows the save-the-app note. */
  isAvailable: boolean;
  /** True until the rows have loaded; with no rows yet, placeholder rows show instead of the empty line. */
  isLoading?: boolean;
  hasRows: boolean;
  /** Error to show under the rows, or ''. */
  error: string;
  /** Short line shown in place of the rows while there are none, such as "No tasks yet." */
  emptyText: string;
  /** Shown at the right of the title, such as a checklist's progress. */
  titleAside?: ReactNode;
  children: ReactNode;
};

/**
 * Shows the title, the rows, and a note or error line. With no rows it keeps the title and shows
 * a short empty line, so people can see the block exists before an action fills it.
 * @param props title and its aside, availability, loading, whether there are rows, the error, the empty line and the rows
 */
export default function StateBlock({
  title,
  selectionPath,
  isAvailable,
  isLoading = false,
  hasRows,
  error,
  emptyText,
  titleAside,
  children,
}: StateBlockProps) {
  return (
    <section>
      {(title || titleAside) && (
        <div className="mb-2 flex items-center justify-between gap-3">
          {title && (
            <h3
              data-fabrik-selection-path={selectionPath ? `${selectionPath}/title` : undefined}
              className="type-body font-semibold"
            >
              {title}
            </h3>
          )}
          {titleAside}
        </div>
      )}
      {hasRows ? (
        children
      ) : isAvailable && isLoading ? (
        <div className="space-y-2 rounded-md border border-border px-3 py-3" role="status">
          <Skeleton className="h-3.5 w-2/3" aria-hidden="true" />
          <Skeleton className="h-3.5 w-1/2" aria-hidden="true" />
          <span className="sr-only">Loading…</span>
        </div>
      ) : (
        isAvailable &&
        !error && (
          <p className="rounded-md border border-dashed border-border px-3 py-3 type-small text-muted-foreground">
            {emptyText}
          </p>
        )
      )}
      {!isAvailable && <p className="mt-2 type-small text-muted-foreground">{SAVE_APP_NOTE}</p>}
      {isAvailable && error && <InlineAlert className="mt-2">{error}</InlineAlert>}
    </section>
  );
}
